import React, { useMemo, useState, useEffect } from "react";
import { DataGrid, GridToolbar} from "@mui/x-data-grid";
import { Box, Divider, ThemeProvider, createTheme } from "@mui/material";
import { styled } from "@mui/system";
import { ALL_INDICATORS, CRP, DP, baseColors } from "../services/util";

const theme = createTheme({
  palette: {
    primary: {
      main: baseColors[1],
    },
  },
});

const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
  "& .MuiDataGrid-columnHeader": {
    whiteSpace: "normal",
    lineHeight: "normal",
    display: "flex",
    justifyContent: "left",
    alignItems: "left",
    textAlign: "left",
    "& .MuiDataGrid-columnHeaderTitle": {
      whiteSpace: "normal",
      lineHeight: "normal",
      textAlign: "center",
      overflow: "visible",
    },
  },
}));

const Datatable = ({ data, showCol, indicators, filters }) => {
  const [filteredData, setFilteredData] = useState(data);
  useEffect(() => {
    let filtered = data;
  
    for (let key in filters) {
      if (filters[key].length > 0) {
        if (key === "Month") {
          filtered = filtered.filter(item => filters[key].includes(
            new Date(item[key]).toLocaleDateString(undefined, { year: "numeric", month: "short" })
          ));
        } else {
          filtered = filtered.filter(item => filters[key].includes(item[key]));
        }
      }
    }
  
    setFilteredData(filtered);
  }, [data, filters]);

  const columns = useMemo(() => {
    if (!filteredData || filteredData.length === 0) return [];
  
    let filteredColumns = Object.keys(filteredData[0]);
  
    if (indicators) {
      const indicatorLabels = indicators.map((indicator) => indicator.label);
      filteredColumns = filteredColumns.filter(
        (key) => indicatorLabels.includes(key) || showCol[key] || key === "Month"
      );
    }
  
    const allIndicatorsMap = new Map(
      ALL_INDICATORS.map((indicator, index) => [indicator.label, index])
    );
      const frontColumns = filteredColumns.filter((key) => !allIndicatorsMap.has(key));
  
    const orderedColumns = ALL_INDICATORS
      .map(indicator => indicator.label)
      .filter(key => filteredColumns.includes(key));
  
    const finalColumns = [...frontColumns, ...orderedColumns];
  
    return finalColumns.map((key) => {
      return {
        field: key,
        headerName: key === "Month" ? "Date" : key,
        flex: 1,
        type: typeof filteredData[0][key],
        align: "center",
        headerAlign: "center",
      };
    });
  }, [filteredData, showCol, indicators]);

  const rows = useMemo(() => {
    if (!filteredData || filteredData.length === 0) return [];
    const formattedRows = filteredData.map((row, index) => ({ id: index, ...row }));
    const options = { year: "numeric", month: "short" };
    formattedRows.forEach((row) => {
      if (row["Month"]) {
        row["Month"] = new Date(row["Month"]).toLocaleDateString(
          undefined,
          options
        );
      }
      if (row["Program"] === CRP.label) {
        row["Program"] = CRP.short 
      }
      if (row["Program"] === DP.label) {
        row["Program"] = DP.short 
      }
    });
    return formattedRows;
  }, [filteredData]);

  const useLatestKeys = ["Total Members", "Social Following"];

  const totals = useMemo(() => {
    if (!filteredData || filteredData.length === 0) return {};
    const summedValues = {};
    const latestValues = {}; // ✅ Store latest values for certain keys

    filteredData.forEach((row) => {
      Object.keys(row).forEach((key) => {
        if (useLatestKeys.includes(key)) {
          latestValues[key] = row[key]; 

        };
        if (typeof row[key] === 'number') {
          if (!summedValues[key]) {
            summedValues[key] = 0;
          }
          summedValues[key] += row[key];
        }
      });
    });
    // add thousands comma
    Object.keys(summedValues).forEach((key) => {
      summedValues[key] = new Intl.NumberFormat().format(summedValues[key]);
    });
  
    return { ...summedValues, ...latestValues };
  }, [filteredData]);

  const totalRow = useMemo(() => {
    const totalRowData = { id: 'total',  ...totals, Month: 'Total'};
    return totalRowData;

  }, [totals]);

  const allRows = useMemo(() => {
    if (!filteredData || filteredData.length === 0) return [];
  
    const formattedRows = rows.concat([totalRow]).map(row => {
      const formattedRow = { ...row };
      Object.keys(formattedRow).forEach(key => {
        if (typeof formattedRow[key] === 'number') {
          formattedRow[key] = new Intl.NumberFormat().format(formattedRow[key]);
        }
      });
      return formattedRow;
    });
  
    return formattedRows;
  }, [filteredData, rows, totalRow]);

  return (
    <ThemeProvider theme={theme}>
    <Box className="visualization-container">
      {!!indicators ? (
        <>
          <Divider />
          <Box className="visualization-description">
            <p className="headline-title">Data Table</p>
            <p className="headline-body">
              This table shows all of the state-specific data for the STAR
              indicators collected since reporting began in November 2023.
            </p>
          </Box>
        </>
      ) : null}
      <Box className="visualization-border visualization-container">
        <div style={{ height: "70vh", width: "100%" }}>
          <StyledDataGrid
            rows={allRows}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[5, 10, 20]}
            getRowId={(row) => row.id}
            slots={{
              toolbar: GridToolbar,
            }}
            getRowClassName={(params) =>
              params.id === 'total' ? 'MuiDataGrid-footerContainer' : ''
            }
          />
        </div>
      </Box>
    </Box>
    </ThemeProvider>
  );
};

export default Datatable;
